/**
 * useBrokerFunds — fetches live margin/funds data for a broker via the API.
 *
 * Calls GET /api/brokers/:brokerId/funds → Cloudflare Worker → VPS MarginDetailRequest.
 * Polls every 90 seconds automatically while the component is mounted.
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { API_BASE } from '../utils/api'

export interface BrokerFunds {
  brokerId: string
  clientId?: string
  netAvailable: number | null
  usedMargin: number | null
  grossAvailable: number | null
  variableMargin?: number | null
  realizedPnl?: number | null
  unrealizedPnl?: number | null
  lastUpdated?: number | null
  error?: string
  loading: boolean
}

const POLL_INTERVAL = 90_000 // 90 seconds

export function useBrokerFunds(brokerId: string | null | undefined): BrokerFunds {
  const { session } = useAuth()
  const [funds, setFunds] = useState<BrokerFunds>({
    brokerId: brokerId || '',
    netAvailable: null,
    usedMargin: null,
    grossAvailable: null,
    loading: true,
  })

  const fetchFunds = useCallback(async () => {
    if (!brokerId || !session?.token) return
    try {
      const res = await fetch(`${API_BASE}/api/brokers/${brokerId}/funds`, {
        headers: { Authorization: `Bearer ${session.token}` },
        signal: AbortSignal.timeout(12000),
      })
      const data = await res.json() as any
      if (!res.ok || data.error) {
        setFunds(prev => ({ ...prev, error: data.error || 'Failed to fetch funds', loading: false }))
        return
      }
      setFunds({
        brokerId,
        clientId: data.clientId,
        netAvailable: data.netAvailable ?? data.net_available ?? null,
        usedMargin: data.usedMargin ?? data.used_margin ?? null,
        grossAvailable: data.grossAvailable ?? data.gross_available ?? null,
        variableMargin: data.variableMargin ?? null,
        realizedPnl: data.realizedPnl ?? null,
        unrealizedPnl: data.unrealizedPnl ?? null,
        lastUpdated: Date.now(),
        loading: false,
      })
    } catch (err: any) {
      setFunds(prev => ({ ...prev, error: 'VPS unreachable', loading: false }))
    }
  }, [brokerId, session?.token])

  useEffect(() => {
    if (!brokerId) return
    fetchFunds()
    const timer = setInterval(fetchFunds, POLL_INTERVAL)
    return () => clearInterval(timer)
  }, [fetchFunds, brokerId])

  return funds
}

// ─── useAllBrokerFunds ───────────────────────────────────────────────────────
// Returns margin data for all connected brokers at once via /api/margin/all

export interface AllBrokerFunds {
  [brokerId: string]: BrokerFunds
}

export function useAllBrokerFunds(): { data: AllBrokerFunds; loading: boolean; refresh: () => void } {
  const { session } = useAuth()
  const [data, setData] = useState<AllBrokerFunds>({})
  const [loading, setLoading] = useState(true)

  const fetchAll = useCallback(async () => {
    if (!session?.token) return
    try {
      const res = await fetch(`${API_BASE}/api/margin/all`, {
        headers: { Authorization: `Bearer ${session.token}` },
        signal: AbortSignal.timeout(10000),
      })
      if (!res.ok) return
      const raw = await res.json() as any
      const mapped: AllBrokerFunds = {}
      for (const [id, v] of Object.entries(raw as Record<string, any>)) {
        mapped[id] = {
          brokerId: id,
          clientId: v.clientId,
          netAvailable: v.netAvailable ?? null,
          usedMargin: v.usedMargin ?? null,
          grossAvailable: v.grossAvailable ?? null,
          lastUpdated: v.lastUpdated ?? null,
          loading: false,
        }
      }
      setData(mapped)
    } catch {
      // silently ignore
    } finally {
      setLoading(false)
    }
  }, [session?.token])

  useEffect(() => {
    fetchAll()
    const timer = setInterval(fetchAll, POLL_INTERVAL)
    return () => clearInterval(timer)
  }, [fetchAll])

  return { data, loading, refresh: fetchAll }
}
